import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { API } from 'aws-amplify';
import { getAPIName } from '../../config';
import handleApiErrors from '../../common/handleApiErrors';
import useDebounce from '../helpers/useDebounce';
import { useSnackbar } from 'notistack';

export interface VehicleData {
  licenseplate: string;
  state: string;
  dealershipname: string;
}

interface VehicleOption {
  id: string;
  licenseplate: string;
  state: string;
  dealershipid: string;
  dealershipname: string;
}

interface LicensePlateSearchProps {
  /** Initial license plate value to display and search for */
  initialLicensePlate: string;
  /** Called when user selects a vehicle from dropdown */
  onSelect: (data: VehicleData) => void;
  /** Called when input is cleared */
  onClear: () => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}
const LicensePlateSearch: React.FC<LicensePlateSearchProps> = ({
  initialLicensePlate,
  onSelect,
  onClear,
  disabled = false,
  error = false,
  helperText = '',
}) => {
  const [inputValue, setInputValue] = useState(initialLicensePlate);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(null);
  const [options, setOptions] = useState<VehicleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedInput = useDebounce(inputValue, 300);
  const { enqueueSnackbar } = useSnackbar();
  const prevInitialPlate = useRef(initialLicensePlate);

  // Update input when initialLicensePlate changes (e.g., opening different violation)
  useEffect(() => {
    if (prevInitialPlate.current !== initialLicensePlate) {
      setInputValue(initialLicensePlate);
      setSelectedVehicle(null);
      prevInitialPlate.current = initialLicensePlate;
    }
  }, [initialLicensePlate]);

  // Fetch vehicles on debounced input change
  useEffect(() => {
    if (!debouncedInput) {
      setOptions([]);
      return;
    }

    const fetchVehicles = async () => {
      try {
        setLoading(true);
        const result = await API.get(
          getAPIName(),
          `/vehicles?search-term=${encodeURIComponent(debouncedInput)}`,
          {}
        );
        const vehicles = Array.isArray(result?.body) ? result.body : [];
        setOptions(
          vehicles.map((v: any) => ({
            id: v.id,
            licenseplate: v.licenseplate,
            state: v.state,
            dealershipid: v.dealershipid,
            dealershipname: v.dealership?.name || '',
          }))
        );
      } catch (err) {
        handleApiErrors(err, enqueueSnackbar);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [debouncedInput, enqueueSnackbar]);

  const handleChange = (_event: any, newValue: VehicleOption | null) => {
    setSelectedVehicle(newValue);
    if (newValue) {
      // Update inputValue to match displayed format
      setInputValue(`${newValue.licenseplate}-${newValue.state}`);
      onSelect({
        licenseplate: newValue.licenseplate,
        state: newValue.state,
        dealershipname: newValue.dealershipname,
      });
    } else {
      setInputValue('');
      onClear();
    }
  };

  const handleInputChange = (_event: any, newInputValue: string, reason: string) => {
    if (reason === 'reset') return;
    if (reason === 'clear') {
      setInputValue('');
      setSelectedVehicle(null);
      onClear();
      return;
    }
    if (!selectedVehicle) {
      setInputValue(newInputValue);
    }
  };

  // Handle keyboard input when a vehicle is selected
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (selectedVehicle) {
      const isModifyingKey = 
        event.key.length === 1 || // Any printable character
        event.key === 'Backspace' || 
        event.key === 'Delete';
      
      if (isModifyingKey) {
        event.preventDefault();
        setInputValue('');
        setSelectedVehicle(null);
        onClear();
      }
    }
  };

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => 
        typeof option === 'string' ? option : `${option.licenseplate}-${option.state}`
      }
      value={selectedVehicle}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      loading={loading}
      disabled={disabled}
      clearOnBlur={false}
      selectOnFocus
      handleHomeEndKeys
      noOptionsText={inputValue ? "No vehicles found" : "Type to search"}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          size="small"
          error={error}
          helperText={helperText}
          placeholder="Type license plate"
          onKeyDown={handleKeyDown}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
            style: { fontSize: 14, fontWeight: 500 },
          }}
        />
      )}
      renderOption={(option) => (
        <div>
          <Typography variant="body2" component="div">
            {option.licenseplate}-{option.state}
          </Typography>
          {option.dealershipname && (
            <Typography variant="caption" color="textSecondary" component="div">
              {option.dealershipname}
            </Typography>
          )}
        </div>
      )}
    />
  );
};

export default LicensePlateSearch;
